/**
 * «Поделиться» файлами — Web Share API level 2.
 *
 * Заменяет собой сразу два действия Android-версии: системный лист «Поделиться»
 * и «Открыть в другом приложении». В вебе положить файл обратно в «Файлы» iOS иначе
 * нельзя — деградация записана в WEB_ROADMAP.md.
 *
 * Правила, без которых лист не открывается на iPhone:
 *  - вызов идёт **строго по жесту**: `navigator.share` из таймера или после `await`,
 *    не связанного с нажатием, Safari отклоняет молча;
 *  - файлы проверяются `canShare` заранее — иначе на неподдерживаемом типе
 *    выбрасывается исключение уже после того, как юрист нажал кнопку;
 *  - отказ пользователя (`AbortError`) — это не ошибка: показывать «не удалось»
 *    в ответ на «отмена» нельзя.
 */

/** Что показывает системный лист: имя, тип и само содержимое. */
export interface ShareTarget {
  fileName: string;
  mimeType: string;
  content: Blob;
}

export type ShareOutcome = 'shared' | 'cancelled' | 'unsupported' | 'failed';

/**
 * Сохраняет файл обычной ссылкой-загрузкой.
 *
 * Запасной путь для архива там, где системного листа нет: в настольном Safari, в браузере
 * без Web Share и в прогоне тестов. На самом iPhone «Поделиться» удобнее — оттуда файл
 * кладут в «Файлы», отправляют себе почтой или в облако, — но выгрузка не имеет права
 * зависеть от одного способа: без неё юрист теряет материалы вместе с иконкой.
 */
export function saveFile(content: Blob, fileName: string): void {
  const url = URL.createObjectURL(content);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();

  // Адрес живёт, пока держит память под файл. Отпускаем не сразу: Safari отменяет
  // загрузку, если освободить адрес в тот же миг, что и щёлкнули по ссылке.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Умеет ли браузер делиться такими файлами. Проверяется до нажатия, а не после. */
export function canShareFiles(files: File[]): boolean {
  if (typeof navigator.share !== 'function') return false;
  if (typeof navigator.canShare !== 'function') return false;

  return navigator.canShare({ files });
}

/** Собирает файлы для листа. Отделено от отправки: сборка асинхронна, отправка — нет. */
export function toShareFiles(targets: ShareTarget[]): File[] {
  return targets.map(
    (target) => new File([target.content], target.fileName, { type: target.mimeType }),
  );
}

/**
 * Открывает системный лист. Вызывается непосредственно из обработчика нажатия.
 *
 * `title` — то, что увидит принимающее приложение в качестве темы письма или имени
 * пересылки. Для одного документа это его название, для пачки — «Документы дела».
 */
export async function shareFiles(files: File[], title: string): Promise<ShareOutcome> {
  if (!canShareFiles(files)) return 'unsupported';

  try {
    await navigator.share({ files, title });
    return 'shared';
  } catch (reason) {
    const name = (reason as { name?: string } | null)?.name;
    return name === 'AbortError' ? 'cancelled' : 'failed';
  }
}
